import React from 'react';

import cx from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import IconButton from 'components/IconButton';

import StatusPill from './StatusPill';
import { PROGRESS_STEPS, SNAPSHOT_STATUSES, TERMINAL_STATUSES } from '../constants';
import styles from '../DbSnapshots.module.scss';

const getStepIndex = (status) => {
  const idx = PROGRESS_STEPS.findIndex((step) => step.key === status);
  if (idx >= 0) return idx;
  if (status === SNAPSHOT_STATUSES.READY) return PROGRESS_STEPS.length - 1;
  return -1;
};

const Stepper = ({ status }) => {
  const currentIndex = getStepIndex(status);
  const isFailed = status === SNAPSHOT_STATUSES.FAILED;

  return (
    <div className={styles.stepper} data-testid='snapshot-stepper'>
      {PROGRESS_STEPS.map((step, idx) => {
        const isComplete = currentIndex > idx || status === SNAPSHOT_STATUSES.READY;
        const isCurrent = currentIndex === idx && status !== SNAPSHOT_STATUSES.READY;
        return (
          <div
            key={step.key}
            className={cx(styles.step, {
              [styles.stepComplete]: isComplete && !isFailed,
              [styles.stepCurrent]: isCurrent,
              [styles.stepFailed]: isFailed && isCurrent,
            })}
          >
            <span className={styles.stepDot} />
            <span className={styles.stepLabel}>{step.label}</span>
          </div>
        );
      })}
    </div>
  );
};

Stepper.propTypes = {
  status: PropTypes.string.isRequired,
};

const SnapshotRow = ({
  id,
  tenant,
  requestedBy,
  status,
  reason,
  failureReason,
  createdAt,
  expiresAt,
  onOpenDetail,
  onDownload,
  onRevoke,
  onRetry,
  canRevoke,
  canRetry,
}) => {
  const isFailed = status === SNAPSHOT_STATUSES.FAILED;
  const isReady = status === SNAPSHOT_STATUSES.READY;
  const isTerminal = TERMINAL_STATUSES.includes(status);

  const handleRowClick = () => onOpenDetail(id);

  const handleRowKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpenDetail(id);
    }
  };

  const handleAction = (event, fn) => {
    event.stopPropagation();
    fn(id);
  };

  return (
    <tr
      onClick={handleRowClick}
      onKeyDown={handleRowKeyDown}
      tabIndex={0}
      role='button'
      aria-label={`View details for ${tenant}`}
      data-testid={`snapshot-row-${id}`}
      className={styles.row}
    >
      <td>
        <p className='font-weight-bold'>{tenant}</p>
        <p className='text-gray-500 font-size-12' title={reason}>
          {reason && reason.length > 60 ? `${reason.slice(0, 60)}…` : reason}
        </p>
      </td>
      <td>
        <p>{requestedBy || '—'}</p>
      </td>
      <td>
        <StatusPill status={status} />
        {isFailed && failureReason && (
          <p className='text-danger font-size-12 mb-0 mt-1' title={failureReason} style={{ maxWidth: 220 }}>
            {failureReason.length > 80 ? `${failureReason.slice(0, 80)}…` : failureReason}
          </p>
        )}
      </td>
      <td>{!isTerminal ? <Stepper status={status} /> : <span className='text-gray-500'>—</span>}</td>
      <td>
        <p>{createdAt ? moment(createdAt).format('YYYY-MM-DD HH:mm') : '—'}</p>
      </td>
      <td>
        <p>{expiresAt ? moment(expiresAt).format('YYYY-MM-DD HH:mm') : '—'}</p>
      </td>
      <td>
        <div className='d-flex gap-2'>
          {isReady && (
            <IconButton
              icon='icon-download'
              size='sm'
              color='primary'
              ariaLabel='Download snapshot'
              onClick={(event) => handleAction(event, onDownload)}
            />
          )}
          {!isTerminal && canRevoke && (
            <IconButton
              icon='icon-trash'
              size='sm'
              color='red'
              ariaLabel='Revoke snapshot'
              onClick={(event) => handleAction(event, onRevoke)}
            />
          )}
          {isFailed && canRetry && (
            <IconButton
              icon='icon-refresh-cw'
              size='sm'
              color='primary'
              ariaLabel='Retry snapshot'
              onClick={(event) => handleAction(event, onRetry)}
            />
          )}
        </div>
      </td>
    </tr>
  );
};

SnapshotRow.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tenant: PropTypes.string.isRequired,
  requestedBy: PropTypes.string,
  status: PropTypes.string.isRequired,
  reason: PropTypes.string,
  failureReason: PropTypes.string,
  createdAt: PropTypes.string,
  expiresAt: PropTypes.string,
  onOpenDetail: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  canRevoke: PropTypes.bool,
  canRetry: PropTypes.bool,
};

SnapshotRow.defaultProps = {
  requestedBy: null,
  reason: '',
  failureReason: null,
  createdAt: null,
  expiresAt: null,
  canRevoke: false,
  canRetry: false,
};

export default SnapshotRow;
